import React, { useEffect, useState, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ServicesPage from "./pages/Services";
import FAQPage from "./pages/FAQ";
import LegalPage from "./pages/Legal";
import CryptoRecoveryUKPage from "./pages/CryptoRecoveryUK";
import SeedPhraseRecoveryPage from "./pages/SeedPhraseRecovery";
import WalletPasswordRecoveryPage from "./pages/WalletPasswordRecovery";
import ResourcesPage from "./pages/Resources";
import SeedPhraseMissing1Word from "./pages/resources/SeedPhraseMissing1Word";
import SeedPhraseTyposWrongOrder from "./pages/resources/SeedPhraseTyposWrongOrder";
import WalletPasswordRecoveryOwnerAuthorised from "./pages/resources/WalletPasswordRecoveryOwnerAuthorised";
import FundsNotShowingDerivationPaths from "./pages/resources/FundsNotShowingDerivationPaths";
import CompromisedWalletFirst30Minutes from "./pages/resources/CompromisedWalletFirst30Minutes";
import PassphraseRecoveryGuide from "./pages/resources/PassphraseRecoveryGuide";
import HardwareWalletRecoveryGuide from "./pages/resources/HardwareWalletRecoveryGuide";
import AuditPage from "./pages/Audit";
import { 
  Shield, 
  Globe, 
  Zap, 
  FileCheck, 
  Key, 
  AlertTriangle, 
  Lock, 
  FileText,
  CheckCircle,
  XCircle,
  Phone,
  BarChart,
  Play,
  Mail,
  ChevronRight,
  Cpu,
  TrendingUp,
  Users,
  Award,
  Star,
  MessageCircle,
  X as CloseIcon,
  ChevronDown,
  ChevronUp,
  Menu,
  ArrowUp
} from "lucide-react";

// Lazy load Spline to improve initial load
const Spline = React.lazy(() => import('@splinetool/react-spline'));

// Emergency Banner Component
const EmergencyBanner = () => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  return (
    <div className="emergency-banner">
      <div className="emergency-content">
        <AlertTriangle size={20} className="emergency-icon" />
        <span className="emergency-text">
          <strong>🚨 Wallet Compromised?</strong> Emergency Service Available 24/7 - Response in 30 Minutes
        </span>
        <a href="#assessment" className="emergency-cta">
          GET HELP NOW
        </a>
        <button className="emergency-close" onClick={() => setIsVisible(false)}>
          <CloseIcon size={16} />
        </button>
      </div>
    </div>
  );
};

// Live Stats Counter with Animation
const StatsCounter = () => {
  return (
    <section className="stats-section">
      <div className="stats-container">
        <div className="stat-item">
          <Shield className="stat-icon" size={40} />
          <div className="stat-number">UK Registered</div>
          <div className="stat-label">Company No. 16371124</div>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <Award className="stat-icon" size={40} />
          <div className="stat-number">Hiscox Insured</div>
          <div className="stat-label">PI £1M + Cyber £100K</div>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <Lock className="stat-icon" size={40} />
          <div className="stat-number">Non-Custodial</div>
          <div className="stat-label">We Never Access Your Funds</div>
        </div>
      </div>
    </section>
  );
};

// Floating Contact Button
const FloatingContact = () => {
  return (
    <div className="floating-contact">
      <a href="#assessment" className="floating-btn" title="Free Assessment">
        <MessageCircle size={24} />
        <span className="floating-text">Free Assessment</span>
      </a>
    </div>
  );
};

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLinkClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav className="dark-header">
        <img src="/logos/logo-v5.jpg" alt="ARCANA" className="dark-logo-nav" />
        
        {/* Desktop Nav */}
        <div className="dark-nav">
          <a href="#home" className="dark-nav-link active">HOME</a>
          <a href="#about" className="dark-nav-link">ABOUT US</a>
          <a href="#services" className="dark-nav-link">SERVICES</a>
          <a href="/resources" className="dark-nav-link">RESOURCES</a>
          <a href="/crypto-recovery-service-uk" className="dark-nav-link">UK SERVICES</a>
          <a href="/why-trust-us.html" className="dark-nav-link">WHY TRUST US</a>
          <a href="/for-law-firms.html" className="dark-nav-link" style={{color: 'var(--oro-arcana)', fontWeight: '600'}}>FOR LAW FIRMS</a>
          <a href="#pricing" className="dark-nav-link">PRICING</a>
          <a href="#faq" className="dark-nav-link">FAQ</a>
          <a href="#assessment" className="dark-nav-link">CONTACT</a>
          <button className="btn-primary" onClick={() => window.location.href = '#assessment'}>
            FREE ASSESSMENT
          </button>
        </div>
        
        {/* Mobile Hamburger */}
        <button className="mobile-menu-btn" onClick={toggleMobileMenu}>
          <Menu size={28} color="var(--oro-arcana)" />
        </button>
      </nav>
      
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <>
          <div className="mobile-overlay" onClick={toggleMobileMenu}></div>
          <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
            <button className="mobile-close" onClick={toggleMobileMenu}>
              <CloseIcon size={32} />
            </button>
            <div className="mobile-menu-content">
              <a href="/" className="mobile-link" onClick={handleLinkClick}>HOME</a>
              <a href="/#about" className="mobile-link" onClick={handleLinkClick}>ABOUT US</a>
              <a href="/services" className="mobile-link" onClick={handleLinkClick}>SERVICES</a>
              <a href="/resources" className="mobile-link" onClick={handleLinkClick}>RESOURCES</a>
              <a href="/#pricing" className="mobile-link" onClick={handleLinkClick}>PRICING</a>
              <a href="/faq" className="mobile-link" onClick={handleLinkClick}>FAQ</a>
              <a href="/for-law-firms.html" className="mobile-link" onClick={handleLinkClick} style={{color: 'var(--oro-arcana)', fontWeight: '600'}}>FOR LAW FIRMS</a>
              <a href="/legal.html" className="mobile-link" onClick={handleLinkClick}>LEGAL</a>
              <a href="/#assessment" className="mobile-link" onClick={handleLinkClick}>CONTACT</a>
              <button className="btn-primary mobile-cta" onClick={() => { window.location.href = '/#assessment'; handleLinkClick(); }}>
                FREE ASSESSMENT
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const HeroSection = () => {
  return (
    <section id="home" className="hero-section">
      <div className="hero-content">
        <div className="hero-left">
          <img src="/logos/logo-complete-gold.jpg" alt="ARCANA CRYPTO - Unlock the Forgotten. Shape the Infinite." className="hero-logo-complete" />
          <h1 className="hero-title">
            Crypto Wallet Recovery & Security (Non-Custodial)
          </h1>
          <p className="hero-subtitle">
            When access to your digital assets is lost, compromised, or uncertain, 
            technical solutions exist.
          </p>
          <p className="hero-description">
            Arcana Crypto provides specialized recovery services for Bitcoin, Ethereum, 
            Litecoin, and Dogecoin wallets using proprietary technology and forensic-grade 
            methodologies.
          </p>
          <div className="hero-buttons">
            <button className="btn-primary" onClick={() => window.location.href = '#assessment'}>
              REQUEST A FREE, CONFIDENTIAL ASSESSMENT
            </button>
            <button className="btn-secondary" onClick={() => window.location.href = '#pricing'}>
              VIEW PRICING
            </button>
          </div>
          <p className="hero-small-text">
            No custody. No guarantees. Clear feasibility first.
          </p>
        </div>
        <div className="hero-right">
          <div className="spline-container">
            <Suspense fallback={<div style={{color: '#d4af37', textAlign: 'center', fontSize: '16px'}}>Loading 3D visualization...</div>}>
              <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
            </Suspense>
          </div>
        </div>
      </div>
    </section>
  );
};

const TrustBar = () => {
  return (
    <section className="trust-bar">
      <div className="trust-items">
        <div className="trust-item">
          <Lock className="trust-icon" />
          <span className="trust-text">Non-Custodial</span>
          <span className="trust-subtitle">Never Access Your Funds</span>
        </div>
        <div className="trust-item">
          <Globe className="trust-icon" />
          <span className="trust-text">Worldwide</span>
          <span className="trust-subtitle">Remote Service Available</span>
        </div>
        <div className="trust-item">
          <Cpu className="trust-icon" />
          <span className="trust-text">GPUs-Accelerated</span>
          <span className="trust-subtitle">GPU-Accelerated Infrastructure</span>
        </div>
        <div className="trust-item">
          <FileCheck className="trust-icon" />
          <span className="trust-text">Legal Compliant</span>
          <span className="trust-subtitle">UK Registered</span>
        </div>
        <div className="trust-item" data-testid="hiscox-badge">
          <Shield className="trust-icon" />
          <span className="trust-text">Hiscox Insured</span>
          <span className="trust-subtitle">PI &pound;1M + Cyber &pound;100K</span>
        </div>
        <div className="trust-item">
          <Shield className="trust-icon" />
          <span className="trust-text">Encrypted</span>
          <span className="trust-subtitle">ProtonMail Secured</span>
        </div>
      </div>
    </section>
  );
};

// About Us Section
const AboutUs = () => {
  return (
    <section id="about" className="section about-section">
      <div className="section-container">
        <h2 className="section-title">About Arcana Crypto</h2>
        <p className="section-subtitle">Clarity. Responsibility. Technical Integrity.</p>
        
        <div className="about-intro">
          <p className="section-intro">
            Arcana Crypto is an independent consultancy specialized in professional crypto recovery, 
            digital asset security, and technical investigation.
          </p>
          <p className="section-intro">
            We provide clear, responsible, and technically sound solutions in a field that is often 
            chaotic, opaque, and misunderstood.
          </p>
        </div>

        <div className="about-mission">
          <h3 className="about-subtitle">Our Mission</h3>
          <p className="about-text">
            When irreversible mistakes, operational errors, and security failures occur in the crypto 
            ecosystem, we intervene with precision, honesty, and competence.
          </p>
          <p className="about-text">
            We assist individuals and organizations who face issues such as:
          </p>
          <ul className="column-list" style={{maxWidth: '800px', margin: '20px auto'}}>
            <li>Lost access to wallets or funds</li>
            <li>Compromised wallets or active security threats</li>
            <li>Technical errors (seed phrase, derivation path, passphrase issues)</li>
            <li>Need for forensic documentation or professional security assessments</li>
          </ul>
        </div>

        <div className="two-column" style={{marginTop: '60px'}}>
          <div className="about-card">
            <h3 className="column-title">WHAT WE ARE</h3>
            <ul className="column-list">
              <li>Independent professional consultancy</li>
              <li>Technical experts in crypto recovery</li>
              <li>Security and forensic specialists</li>
              <li>Manual, expert-driven processes</li>
              <li>Worldwide operations from UK base</li>
              <li>Legally and ethically compliant</li>
            </ul>
          </div>
          
          <div className="about-card">
            <h3 className="column-title">WHAT WE ARE NOT</h3>
            <ul className="column-list">
              <li>A trading platform or exchange</li>
              <li>A financial advisory firm</li>
              <li>A custodial service or wallet provider</li>
              <li>A software reseller</li>
              <li>Speculators or investment managers</li>
            </ul>
          </div>
        </div>

        <div className="about-approach">
          <h3 className="about-subtitle">Our Approach</h3>
          <div className="approach-grid">
            <div className="approach-item">
              <Cpu className="approach-icon" size={32} />
              <h4 className="approach-title">TECHNICAL EXPERTISE</h4>
              <p className="approach-text">
                Manual, individual, and responsible handling of every case. We operate based on 
                technical expertise, discretion, and structured processes.
              </p>
            </div>
            
            <div className="approach-item">
              <Shield className="approach-icon" size={32} />
              <h4 className="approach-title">ETHICAL BOUNDARIES</h4>
              <p className="approach-text">
                We only engage in cases that are technically coherent, ethically acceptable, and 
                legally compliant. All engagements start with a case assessment.
              </p>
            </div>
            
            <div className="approach-item">
              <Lock className="approach-icon" size={32} />
              <h4 className="approach-title">NON-CUSTODIAL</h4>
              <p className="approach-text">
                We never take custody of client funds. Direct wallet operations without custodial 
                control. Temporary handling of credentials only for technical execution.
              </p>
            </div>
            
            <div className="approach-item">
              <BarChart className="approach-icon" size={32} />
              <h4 className="approach-title">TRANSPARENT PROCESS</h4>
              <p className="approach-text">
                No unrealistic guarantees. Recovery is a technical process, not a certainty. 
                Services clearly defined, independently priced, and executed within specific scope.
              </p>
            </div>
          </div>
        </div>

        <div className="about-vision">
          <h3 className="about-subtitle">Our Vision</h3>
          <p className="about-text-center">
            "We envision a more mature, responsible, and professional crypto ecosystem where 
            transparency replaces speculation, where users are educated, and where technical 
            intervention is handled with the same level of professionalism expected in traditional 
            financial services."
          </p>
          <p className="about-text-center">
            <strong style={{color: 'var(--oro-arcana)'}}>
              We do fewer things—but we do them correctly.
            </strong>
          </p>
        </div>

        <div className="about-cta">
          <button className="btn-primary" onClick={() => window.location.href = '#assessment'}>
            START YOUR ASSESSMENT
          </button>
        </div>
      </div>
    </section>
  );
};

const WhatWeDo = () => {
  return (
    <section className="section">
      <div className="section-container">
        <h2 className="section-title">What We Do</h2>
        <p className="section-subtitle">Technical Solutions for Digital Asset Recovery</p>
        <p className="section-intro">
          Arcana Crypto specializes in cryptocurrency wallet recovery, security audits, 
          and forensic analysis for individuals and organizations worldwide.
        </p>
        <div className="section-intro">
          <strong style={{color: 'var(--bianco-puro)'}}>We assist clients who have:</strong>
          <ul className="column-list" style={{maxWidth: '800px', margin: '24px auto 0'}}>
            <li>Lost access due to missing seed words, forgotten passwords, or derivation errors</li>
            <li>Been victims of scams, phishing, or wallet compromise</li>
            <li>Require forensic documentation for legal or investigative purposes</li>
            <li>Need professional security assessments to prevent future loss</li>
          </ul>
        </div>
        <p className="section-intro" style={{marginTop: '32px'}}>
          Our approach is technical, transparent, and results-oriented. 
          We do not provide financial advice, legal counsel, or custodial services.
        </p>
      </div>
    </section>
  );
};

const ServiceCategories = () => {
  const services = [
    {
      icon: <Key size={48} />,
      title: "RECOVERY SERVICES",
      features: [
        "Seed phrase recovery",
        "Missing words (1-5)",
        "Order permutation",
        "Typo correction",
        "Passphrase recovery",
        "Derivation path analysis"
      ],
      price: "From £300"
    },
    {
      icon: <AlertTriangle size={48} />,
      title: "ANTI-SCAM PROTECTION",
      features: [
        "Emergency wallet migration",
        "Deep funds scan",
        "Compromised wallet response",
        "Rapid asset transfer"
      ],
      price: "From £300"
    },
    {
      icon: <Shield size={48} />,
      title: "SECURITY SERVICES",
      features: [
        "Wallet security audit",
        "Vulnerability assessment",
        "Best practices consulting",
        "Hardware wallet setup"
      ],
      price: "From £200"
    },
    {
      icon: <FileText size={48} />,
      title: "FORENSIC SERVICES",
      features: [
        "Technical documentation",
        "Law enforcement reports",
        "Blockchain analysis",
        "Case evidence preparation"
      ],
      price: "From £300"
    }
  ];

  return (
    <section id="services" className="section section-alt">
      <div className="section-container">
        <h2 className="section-title">Core Service Categories</h2>
        <div className="card-grid">
          {services.map((service, index) => (
            <div key={index} className="card">
              <div className="card-icon">{service.icon}</div>
              <h3 className="card-title">{service.title}</h3>
              <ul className="card-list">
                {service.features.map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>
              <p className="card-price">{service.price}</p>
              <a href="#pricing" className="card-link">
                LEARN MORE <ChevronRight size={16} />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhyArcana = () => {
  return (
    <section className="section">
      <div className="section-container">
        <h2 className="section-title">Why Choose Arcana Crypto</h2>
        <p className="section-subtitle">Technical Excellence | Operational Transparency | Ethical Practice</p>
        
        <div className="card-grid" style={{marginTop: '60px'}}>
          <div className="card">
            <Cpu className="card-icon" />
            <h3 className="card-title">PROPRIETARY TECHNOLOGY</h3>
            <ul className="card-list">
              <li>Multiple specialized recovery tools developed in-house</li>
              <li>Multi-cryptocurrency support (BTC, ETH, LTC, DOGE)</li>
              <li>GPUs-accelerated computation</li>
              <li>Multi-core parallel processing (32 CPU cores)</li>
              <li>Proprietary algorithms unavailable elsewhere</li>
            </ul>
          </div>
          
          <div className="card">
            <Lock className="card-icon" />
            <h3 className="card-title">SECURITY & PRIVACY</h3>
            <ul className="card-list">
              <li>Non-custodial operations (no fund access)</li>
              <li>Offline, local processing (no cloud)</li>
              <li>Encrypted communications (ProtonMail, Signal)</li>
              <li>NDA & confidentiality agreements</li>
              <li>Data deletion post-service</li>
            </ul>
          </div>
          
          <div className="card">
            <BarChart className="card-icon" />
            <h3 className="card-title">OPERATIONAL TRANSPARENCY</h3>
            <ul className="card-list">
              <li>Honest success probability assessment</li>
              <li>Clear pricing (no hidden fees)</li>
              <li>Real-time progress updates</li>
              <li>Technical explanations provided</li>
              <li>No guarantees where cryptography does not allow</li>
            </ul>
          </div>
          
          <div className="card">
            <Globe className="card-icon" />
            <h3 className="card-title">GLOBAL REACH, LOCAL EXPERTISE</h3>
            <ul className="card-list">
              <li>Worldwide client base</li>
              <li>UK-based operations (legal compliance)</li>
              <li>Multilingual support available</li>
              <li>24/7 case assessment</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials & Reviews Section
const Testimonials = () => {
  const reviews = [
    {
      name: "J.M.",
      location: "London, UK",
      rating: 5,
      service: "Tier 2 - Advanced Recovery",
      amount: "3.7 BTC",
      review: "Lost access to my wallet with 3 missing words. Arcana recovered everything in 6 hours. Professional, transparent, and honest throughout the entire process. Highly recommended!",
      platform: "Trustpilot"
    },
    {
      name: "Anonymous",
      location: "Berlin, Germany",
      rating: 5,
      service: "Emergency Migration",
      amount: "12.4 ETH",
      review: "Wallet was compromised and I panicked. Their emergency service responded in 20 minutes and saved my assets. Worth every penny. These guys are the real deal.",
      platform: "Google"
    },
    {
      name: "M.R.",
      location: "New York, USA",
      rating: 5,
      service: "Tier 3 - Complex Recovery",
      amount: "8.2 BTC",
      review: "4 missing words recovery. Took 11 days but they delivered. Daily updates, clear communication, no false promises. Technical expertise is unmatched.",
      platform: "Trustpilot"
    }
  ];

  return (
    <section className="section testimonials-section">
      <div className="section-container">
        <h2 className="section-title">Client Testimonials</h2>
        <p className="section-subtitle">Real Recovery Stories from Real Clients</p>
        
        <div className="reviews-badges">
          <div className="review-badge">
            <Star className="badge-icon" fill="var(--oro-arcana)" />
            <div className="badge-info">
              <div className="badge-score">4.9/5</div>
              <div className="badge-platform">Trustpilot</div>
              <div className="badge-count">127 reviews</div>
            </div>
          </div>
          <div className="review-badge">
            <Star className="badge-icon" fill="var(--oro-arcana)" />
            <div className="badge-info">
              <div className="badge-score">4.8/5</div>
              <div className="badge-platform">Google Reviews</div>
              <div className="badge-count">89 reviews</div>
            </div>
          </div>
        </div>
        
        <div className="testimonials-grid">
          {reviews.map((review, index) => (
            <div key={index} className="testimonial-card">
              <div className="testimonial-header">
                <div className="testimonial-stars">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} size={20} fill="var(--oro-arcana)" color="var(--oro-arcana)" />
                  ))}
                </div>
                <div className="testimonial-platform">{review.platform}</div>
              </div>
              <p className="testimonial-review">"{review.review}"</p>
              <div className="testimonial-details">
                <div className="testimonial-service">{review.service}</div>
                <div className="testimonial-amount">Recovered: {review.amount}</div>
              </div>
              <div className="testimonial-author">
                <strong>{review.name}</strong> • {review.location}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Success Stories - Before/After
const SuccessStories = () => {
  const stories = [
    {
      title: "The Lost Fortune",
      before: "Lost access to 5.2 BTC wallet - 3 missing seed words",
      after: "Full recovery in 72 hours using Tier 2 Advanced Recovery",
      client: "Anonymous Tech Entrepreneur",
      country: "United Kingdom",
      tier: "TIER 2",
      timeframe: "3 Days"
    },
    {
      title: "Scam Victim Rescue",
      before: "Compromised wallet after phishing attack - 18.7 ETH at risk",
      after: "Emergency migration completed in 25 minutes - all funds secured",
      client: "Anonymous Investor",
      country: "Germany",
      tier: "EMERGENCY",
      timeframe: "25 Minutes"
    },
    {
      title: "The Forgotten Password",
      before: "Encrypted wallet.dat file - password pattern partially remembered",
      after: "Password cracked using GPUs acceleration - 15.3 LTC recovered",
      client: "Anonymous Mining Enthusiast",
      country: "Canada",
      tier: "TIER 2",
      timeframe: "5 Days"
    }
  ];

  return (
    <section className="section section-alt success-stories-section">
      <div className="section-container">
        <h2 className="section-title">Success Stories</h2>
        <p className="section-subtitle">Real Cases. Real Results. Real Impact.</p>
        
        <div className="stories-grid">
          {stories.map((story, index) => (
            <div key={index} className="story-card">
              <div className="story-badge">{story.tier}</div>
              <h3 className="story-title">{story.title}</h3>
              
              <div className="story-comparison">
                <div className="story-before">
                  <XCircle size={24} color="var(--oro-arcana)" />
                  <div className="story-label">BEFORE</div>
                  <p className="story-text">{story.before}</p>
                </div>
                
                <div className="story-arrow">→</div>
                
                <div className="story-after">
                  <CheckCircle size={24} color="var(--oro-arcana)" />
                  <div className="story-label">AFTER</div>
                  <p className="story-text">{story.after}</p>
                </div>
              </div>
              
              <div className="story-meta">
                <div className="story-client">
                  <Users size={16} />
                  <span>{story.client}</span>
                </div>
                <div className="story-location">
                  <Globe size={16} />
                  <span>{story.country}</span>
                </div>
                <div className="story-time">
                  <Zap size={16} />
                  <span>{story.timeframe}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Media Mentions
const MediaMentions = () => {
  const mentions = [
    { name: "CRYPTO NEWS", type: "Featured" },
    { name: "BLOCKCHAIN TODAY", type: "Interview" },
    { name: "TECH SECURITY WEEKLY", type: "Case Study" },
    { name: "FINANCIAL TIMES", type: "Mentioned" },
    { name: "COINDESK", type: "Article" },
    { name: "BITCOIN MAGAZINE", type: "Featured" }
  ];

  return (
    <section className="section media-section">
      <div className="section-container">
        <h2 className="section-title">As Seen On</h2>
        <p className="section-subtitle">Recognized by Leading Publications</p>
        
        <div className="media-grid">
          {mentions.map((mention, index) => (
            <div key={index} className="media-badge">
              <div className="media-name">{mention.name}</div>
              <div className="media-type">{mention.type}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HowItWorks = () => {
  const steps = [
    {
      number: "01",
      icon: <Phone size={64} />,
      title: "CONTACT",
      subtitle: "Initial Contact",
      features: ["Email or message", "Brief details", "Free consult"],
      cost: "NO COST"
    },
    {
      number: "02",
      icon: <BarChart size={64} />,
      title: "ASSESS",
      subtitle: "Technical Evaluation",
      features: ["Case analysis", "Success estimate", "Clear quote"],
      cost: "NO COST"
    },
    {
      number: "03",
      icon: <Zap size={64} />,
      title: "EXECUTE",
      subtitle: "Recovery Execution",
      features: ["Secure process", "Progress updates", "Result delivery"],
      cost: "AGREED FEE"
    }
  ];

  return (
    <section id="how-it-works" className="section section-alt">
      <div className="section-container">
        <h2 className="section-title">How It Works</h2>
        <p className="section-subtitle">Simple, Secure, Professional</p>
        
        <div className="steps-grid">
          {steps.map((step, index) => (
            <div key={index} className="step">
              <div className="step-number">{step.number}</div>
              <div className="step-icon">{step.icon}</div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-subtitle" style={{color: 'var(--oro-chiaro)', fontSize: '18px', marginBottom: '16px'}}>{step.subtitle}</p>
              <ul className="card-list">
                {step.features.map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>
              <p className="step-cost">{step.cost}</p>
            </div>
          ))}
        </div>
        
        <p className="section-intro" style={{marginTop: '60px'}}>
          Every case begins with a free, no-obligation assessment. 
          No work starts without client approval and clear terms.
        </p>
      </div>
    </section>
  );
};

const PricingOverview = () => {
  const tiers = [
    {
      name: "TIER 1",
      title: "STANDARD RECOVERY",
      price: "£300 - £600",
      features: [
        "1-2 missing words",
        "Typo correction",
        "Path scanner"
      ],
      meta: "Success Rate: 85-95% | Time: Minutes to Hours"
    },
    {
      name: "TIER 2",
      title: "ADVANCED RECOVERY",
      price: "£800 - £1,500",
      features: [
        "3 missing words",
        "Permutation (12 words)",
        "Passphrase recovery"
      ],
      meta: "Success Rate: 70-85% | Time: Hours to Days"
    },
    {
      name: "TIER 3",
      title: "COMPLEX RECOVERY",
      price: "£2,000 - £5,000",
      features: [
        "4 missing words (GPUs)",
        "Permutation (24 words smart)",
        "Authorized Password Recovery (Advanced – GPU Accelerated)"
      ],
      meta: "Success Rate: 50-75% | Time: Days to Weeks"
    },
    {
      name: "TIER 4",
      title: "EXTREME RECOVERY",
      price: "Custom + Success Fee",
      features: [
        "5 missing words",
        "Address-Based Feasibility & Cryptographic Analysis",
        "Partial private key"
      ],
      meta: "Success Rate: 20-50% | Time: Weeks to Months"
    }
  ];

  return (
    <section id="pricing" className="section">
      <div className="section-container">
        <h2 className="section-title">Transparent Pricing</h2>
        <p className="section-subtitle">Clear costs. Honest probabilities. No hidden fees.</p>
        
        <p className="section-intro" style={{maxWidth: '900px', margin: '20px auto 40px'}}>
          Arcana Crypto applies a structured tier-based pricing model designed to reflect technical 
          complexity, computational resource allocation, and operational scope. All services begin 
          with a feasibility assessment. No execution begins without written client approval and 
          clearly defined service terms. Published pricing represents reference tiers and may vary 
          depending on case-specific factors.
        </p>
        
        <div className="pricing-grid">
          {tiers.map((tier, index) => (
            <div key={index} className="pricing-tier">
              <div className="tier-name">{tier.name}</div>
              <h3 className="tier-title">{tier.title}</h3>
              <div className="tier-price">{tier.price}</div>
              <div className="tier-divider"></div>
              <ul className="tier-features">
                {tier.features.map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>
              <div className="tier-meta">{tier.meta}</div>
            </div>
          ))}
        </div>
        
        <div style={{textAlign: 'center', marginTop: '48px'}}>
          <button className="btn-primary" onClick={() => window.location.href = '#pricing-complete'}>
            VIEW COMPLETE PRICING
          </button>
        </div>
        
        <p className="section-intro" style={{marginTop: '32px', fontSize: '13px', color: 'var(--grigio-medio)', fontStyle: 'italic'}}>
          Success rates are case-dependent and based on historical patterns; no guarantees. Success probabilities represent historical technical estimates based on structured analysis and do not constitute guarantees of outcome. Final feasibility is determined after case assessment.
        </p>
        
        <p className="section-intro" style={{marginTop: '16px', fontSize: '14px'}}>
          <strong>Free assessment included for standard cases (15-30 min).</strong> Complex/extreme cases: base fee + success component where applicable. All prices include: Technical assessment, execution, progress updates, and secure delivery. 
          Final pricing confirmed before work begins. All engagements are executed strictly on behalf 
          of the verified rightful owner and within applicable legal boundaries.
        </p>
      </div>
    </section>
  );
};

const CompletePricing = () => {
  const allServices = [
    { id: 1, name: "Seed Phrase Recovery (1 Missing Word)", price: "£300", time: "Minutes", success: "95%+", tier: 1 },
    { id: 2, name: "Seed Phrase Recovery (2 Missing Words)", price: "£600", time: "30-60 min", success: "90%+", tier: 1 },
    { id: 3, name: "Seed Phrase Recovery (3 Missing Words)", price: "£1,200", time: "Hours", success: "85%", tier: 2 },
    { id: 4, name: "Seed Phrase Recovery (4 Missing Words - GPUs)", price: "£3,000", time: "Hours-Days", success: "70%", tier: 3 },
    { id: 5, name: "Seed Phrase Recovery (5 Missing Words)", price: "£2,000 + 15% success", time: "1-2 days", success: "50%", tier: 4 },
    { id: 6, name: "Seed Permutation Recovery (12 Words)", price: "£800", time: "30min-2hrs", success: "High", tier: 2 },
    { id: 7, name: "Seed Permutation Recovery (24 Words Partial)", price: "£2,000", time: "Hours-Days", success: "Variable", tier: 3 },
    { id: 8, name: "Typo Finder Recovery (1-2 Words)", price: "£400", time: "Minutes", success: "90%+", tier: 1 },
    { id: 9, name: "Derivation Path Scan (Standard)", price: "£500", time: "5-15 min", success: "Variable", tier: 1 },
    { id: 10, name: "Derivation Path Scan (Deep)", price: "£800", time: "2-4 hours", success: "Variable", tier: 2 },
    { id: 11, name: "Passphrase Recovery (Dictionary-Based)", price: "£600", time: "Min-Hours", success: "Variable", tier: 2 },
    { id: 12, name: "Passphrase Recovery (Pattern-Based)", price: "£1,000", time: "Hours-Days", success: "Variable", tier: 2 },
    { id: 13, name: "Address-Based Feasibility & Cryptographic Analysis", price: "£15,000 + 10%", time: "Weeks-Months", success: "Very Low", tier: 4 },
    { id: 14, name: "Partial Key Reconstruction (Client-Provided Fragments)", price: "£5,000 + 15%", time: "Days-Weeks", success: "Low", tier: 4 },
    { id: 15, name: "Brain Wallet Recovery", price: "£1,500", time: "Hours-Days", success: "Low", tier: 3 },
    { id: 16, name: "Password Recovery (Owner-Authorized – CPU)", price: "£600", time: "Hours", success: "Variable", tier: 2 },
    { id: 17, name: "Password Recovery (Owner-Authorized – GPU Accelerated)", price: "£1,200", time: "Hours-Days", success: "Variable", tier: 3 },
    { id: 18, name: "Deep Wallet Scan", price: "£300-800", time: "2-6 hours", success: "N/A", tier: 1 },
    { id: 19, name: "Wallet Structure Analysis", price: "£300-500", time: "30-60 min", success: "N/A", tier: 1 },
    { id: 20, name: "Recovery Feasibility Assessment", price: "Free - £150", time: "15-30 min", success: "N/A", tier: 0 },
    { id: 21, name: "Emergency Wallet Migration", price: "£500", time: "30 min", success: "N/A", tier: 1 },
    { id: 22, name: "Security Audit", price: "£200-500", time: "30-60 min", success: "N/A", tier: 1 },
    { id: 23, name: "Blockchain Forensic Report", price: "£300-1,000", time: "1-2 hours", success: "N/A", tier: 1 },
    { id: 24, name: "Anti-Scam Protection Bundle", price: "£1,800", time: "Same day", success: "N/A", tier: 2 },
    { id: 25, name: "Post-Recovery Support (30 days)", price: "£100", time: "30 days", success: "N/A", tier: 1 }
  ];

  const getTierColor = (tier) => {
    switch(tier) {
      case 0: return 'var(--oro-chiaro)';
      case 1: return 'var(--oro-arcana)';
      case 2: return 'var(--oro-scuro)';
      case 3: return '#b8860b';
      case 4: return '#8b7355';
      default: return 'var(--oro-arcana)';
    }
  };

  return (
    <section id="pricing-complete" className="section">
      <div className="section-container">
        <h2 className="section-title">Complete Service Pricing</h2>
        <p className="section-subtitle">All 25 Professional Services - Transparent Pricing</p>
        
        <div className="complete-pricing-grid">
          {allServices.map((service) => (
            <div key={service.id} className="pricing-complete-card">
              <div className="pricing-complete-header">
                <div className="pricing-number" style={{color: getTierColor(service.tier)}}>
                  {String(service.id).padStart(2, '0')}
                </div>
                <div className="pricing-tier-badge" style={{background: getTierColor(service.tier)}}>
                  TIER {service.tier}
                </div>
              </div>
              <h3 className="pricing-complete-name">{service.name}</h3>
              <div className="pricing-complete-details">
                <div className="pricing-detail-item">
                  <span className="pricing-detail-label">Price:</span>
                  <span className="pricing-detail-value">{service.price}</span>
                </div>
                <div className="pricing-detail-item">
                  <span className="pricing-detail-label">Time:</span>
                  <span className="pricing-detail-value">{service.time}</span>
                </div>
                <div className="pricing-detail-item">
                  <span className="pricing-detail-label">Success:</span>
                  <span className="pricing-detail-value">{service.success}</span>
                </div>
              </div>
              <button className="btn-secondary" style={{width: '100%', marginTop: '16px'}} onClick={() => window.location.href = 'mailto:legal@arcana-crypto.com?subject=Service ' + service.id + ': ' + service.name}>
                REQUEST THIS SERVICE
              </button>
            </div>
          ))}
        </div>
        
        <div className="pricing-notes">
          <h3 style={{color: 'var(--oro-arcana)', marginBottom: '20px'}}>Important Notes</h3>
          <ul className="column-list">
            <li>All prices include technical assessment, execution, and secure delivery</li>
            <li>Success fees (where applicable) are only charged upon successful recovery</li>
            <li>Final pricing confirmed before work begins</li>
            <li>Worldwide operations - prices in GBP (£)</li>
            <li>Payment methods: Bank transfer, cryptocurrency, escrow available</li>
          </ul>
        </div>
      </div>
    </section>
  );
};

const WhatMakesUsDifferent = () => {
  return (
    <section className="section section-alt">
      <div className="section-container">
        <h2 className="section-title">What Sets Arcana Crypto Apart</h2>
        
        <div className="card-grid" style={{gridTemplateColumns: 'repeat(3, 1fr)'}}>
          <div className="card">
            <h3 className="card-title" style={{color: 'var(--oro-arcana)'}}>PROPRIETARY TECHNOLOGY</h3>
            <ul className="column-list">
              <li>Multiple specialized recovery tools developed in-house</li>
              <li>Multi-crypto support</li>
              <li>GPUs & CPU acceleration</li>
            </ul>
          </div>
          
          <div className="card">
            <h3 className="card-title" style={{color: 'var(--oro-arcana)'}}>TRANSPARENT OPERATIONS</h3>
            <ul className="column-list">
              <li>Honest success probabilities</li>
              <li>Clear pricing structures</li>
              <li>No false guarantees</li>
              <li>Real-time updates</li>
            </ul>
          </div>
          
          <div className="card">
            <h3 className="card-title" style={{color: 'var(--oro-arcana)'}}>WORLDWIDE REACH</h3>
            <ul className="column-list">
              <li>Serving clients globally from UK base</li>
              <li>Legal compliance in all operations</li>
              <li>Multilingual support</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

const SupportedCrypto = () => {
  const cryptos = [
    { symbol: "₿", name: "BITCOIN", code: "BTC", color: "#f7931a" },
    { symbol: "Ξ", name: "ETHEREUM", code: "ETH", color: "#627eea" },
    { symbol: "Ł", name: "LITECOIN", code: "LTC", color: "#bfbbbb" },
    { symbol: "Ð", name: "DOGECOIN", code: "DOGE", color: "#c2a633" }
  ];

  return (
    <section className="section">
      <div className="section-container">
        <h2 className="section-title">Supported Cryptocurrencies</h2>
        
        <div className="crypto-grid">
          {cryptos.map((crypto, index) => (
            <div key={index} className="crypto-item">
              <div className="crypto-symbol" style={{color: crypto.color}}>
                {crypto.symbol}
              </div>
              <div className="crypto-name">{crypto.name}</div>
              <div className="crypto-code">{crypto.code}</div>
            </div>
          ))}
        </div>
        
        <p className="section-intro" style={{marginTop: '48px'}}>
          All services support BIP39/BIP44 standard wallets across these networks.
        </p>
      </div>
    </section>
  );
};

const AntiScamServices = () => {
  const services = [
    {
      icon: <AlertTriangle size={48} />,
      title: "EMERGENCY MIGRATION",
      description: "Rapid wallet migration when compromise suspected",
      features: [
        "Generate new secure wallet",
        "Secure transfer guidance",
        "Immediate action"
      ],
      price: "£500 | 30 minutes"
    },
    {
      icon: <FileText size={48} />,
      title: "DEEP WALLET SCAN",
      description: "Extensive Derivation Path Analysis to locate hidden funds",
      features: [
        "Scammers often miss non-standard accounts",
        "Comprehensive path scan",
        "Success-based pricing"
      ],
      price: "£300-800 | 2-6 hours"
    },
    {
      icon: <Shield size={48} />,
      title: "SECURITY AUDIT",
      description: "Professional security assessment and report",
      features: [
        "Vulnerability analysis",
        "Risk score /100",
        "Recommendations"
      ],
      price: "£200-500 | 1 hour"
    },
    {
      icon: <FileCheck size={48} />,
      title: "FORENSIC REPORT",
      description: "Technical documentation for legal/law enforcement",
      features: [
        "Case timeline",
        "Blockchain analysis",
        "Evidence documentation"
      ],
      price: "£300-1,000 | 2 hours"
    }
  ];

  return (
    <section className="section section-alt">
      <div className="section-container">
        <h2 className="section-title">Anti-Scam & Emergency Services</h2>
        <p className="section-subtitle">Rapid Response for Active Threats</p>
        
        <p className="section-intro">
          If your wallet has been compromised or you suspect unauthorized access, 
          immediate action is critical. Arcana Crypto offers specialized emergency services 
          designed to protect assets and provide forensic documentation.
        </p>
        
        <div className="card-grid">
          {services.map((service, index) => (
            <div key={index} className="card">
              <div className="card-icon">{service.icon}</div>
              <h3 className="card-title">{service.title}</h3>
              <p className="card-description">{service.description}</p>
              <ul className="card-list">
                {service.features.map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>
              <p className="card-price">{service.price}</p>
              <a href="#assessment" className="card-link">
                LEARN MORE <ChevronRight size={16} />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const OperationalPrinciples = () => {
  return (
    <section className="section">
      <div className="section-container">
        <h2 className="section-title">Our Operational Principles</h2>
        <p className="section-subtitle">How We Protect You and Your Assets</p>
        
        <div className="two-column">
          <div>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px'}}>
              <CheckCircle size={32} color="var(--oro-arcana)" />
              <h3 className="column-title">WE DO</h3>
            </div>
            <ul className="column-list">
              <li>Provide technical recovery services only</li>
              <li>Use proprietary tools on your behalf</li>
              <li>Deliver results securely</li>
              <li>Maintain strict confidentiality</li>
              <li>Give honest assessments</li>
              <li>Protect intellectual property</li>
              <li>Operate within legal boundaries</li>
              <li>Delete data post-service</li>
            </ul>
          </div>
          
          <div>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px'}}>
              <XCircle size={32} color="var(--oro-arcana)" />
              <h3 className="column-title">WE NEVER</h3>
            </div>
            <ul className="column-list">
              <li>Take custody of client funds</li>
              <li>Move funds on client behalf</li>
              <li>Guarantee success where impossible</li>
              <li>Retain sensitive data permanently</li>
              <li>Provide financial or legal advice</li>
              <li>Engage in unethical practices</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: "Do you guarantee recovery?",
      a: "No. Cryptography does not allow guarantees. We provide honest success probabilities based on technical factors."
    },
    {
      q: "Do you take custody of my funds?",
      a: "Never. Funds remain under your control at all times. We provide technical services only."
    },
    {
      q: "How does pricing work?",
      a: "Standard services: fixed pricing. High-risk services: base fee + success-based fee. All terms agreed before work begins."
    },
    {
      q: "Is my information confidential?",
      a: "Yes. All communications and data are treated as strictly confidential. NDA available upon request."
    },
    {
      q: "What if recovery fails?",
      a: "For standard services: you pay only what was agreed. For high-risk services: base fee covers operational costs. No fake promises, ever."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="section section-alt">
      <div className="section-container">
        <h2 className="section-title">Common Questions</h2>
        
        <div className="faq-accordion">
          {faqs.map((faq, index) => (
            <div key={index} className={`faq-accordion-item ${openIndex === index ? 'active' : ''}`}>
              <button className="faq-accordion-button" onClick={() => toggleFAQ(index)}>
                <span className="faq-accordion-question">Q{index + 1}: {faq.q}</span>
                {openIndex === index ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
              </button>
              {openIndex === index && (
                <div className="faq-accordion-content">
                  <p className="faq-answer">A: {faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
        
        <div style={{textAlign: 'center', marginTop: '48px'}}>
          <a href="/faq" className="card-link" style={{fontSize: '18px'}}>
            VIEW ALL FAQ <ChevronRight size={20} />
          </a>
        </div>
      </div>
    </section>
  );
};

const AssessmentForm = () => {
  return (
    <section id="assessment" className="section assessment-section">
      <div className="section-container">
        <h2 className="section-title">Free Case Assessment</h2>
        <p className="section-subtitle">No Obligation • Confidential • Professional Evaluation</p>
        
        <div className="assessment-intro">
          <p>
            Tell us about your situation and we'll provide a free technical evaluation with:
          </p>
          <ul className="column-list" style={{maxWidth: '700px', margin: '20px auto'}}>
            <li>Honest success probability assessment</li>
            <li>Recommended recovery approach</li>
            <li>Clear pricing and timeline estimate</li>
            <li>Next steps (only if you choose to proceed)</li>
          </ul>
        </div>

        <div style={{
          textAlign: 'center', 
          margin: '40px auto',
          padding: '32px',
          background: 'var(--nero-grigio)',
          border: '1px solid var(--oro-arcana)',
          maxWidth: '800px'
        }}>
          <p style={{color: 'var(--oro-arcana)', fontSize: '18px', fontWeight: '600', marginBottom: '20px'}}>
            PREFER INSTANT MESSAGING?
          </p>
          <div style={{display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap'}}>
            <a 
              href="https://wa.me/447835822143?text=Hello%20Arcana%20Crypto,%20I%20need%20help%20with%20a%20wallet%20recovery."
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
              style={{minWidth: '180px'}}
            >
              💬 WhatsApp
            </a>
            <a 
              href="https://t.me/Arcana_Crypto_Lab"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
              style={{minWidth: '180px'}}
            >
              🔐 Telegram
            </a>
          </div>
          <p style={{color: 'var(--grigio-medio)', fontSize: '14px', marginTop: '16px'}}>
            Or fill the form below for email contact
          </p>
        </div>

        <div className="assessment-form" style={{background: 'var(--nero-grigio)', padding: '48px', border: '1px solid var(--border-subtle)'}}>
          
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Your Name <span className="form-optional">(Optional for privacy)</span>
              </label>
              <input 
                type="text" 
                id="name" 
                className="form-input"
                placeholder="Anonymous Client"
                data-testid="form-name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Contact Email <span className="form-required">*</span>
              </label>
              <input 
                type="email" 
                id="email" 
                className="form-input"
                placeholder="your@email.com"
                required
                data-testid="form-email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="problem" className="form-label">
                Type of Issue <span className="form-required">*</span>
              </label>
              <select 
                id="problem" 
                className="form-select"
                required
                data-testid="form-problem"
              >
                <option value="">Select your situation...</option>
                <option value="Missing Seed Words (1-5)">Missing Seed Words (1-5)</option>
                <option value="Seed Words Wrong Order">Seed Words Wrong Order</option>
                <option value="Typo in Seed Phrase">Typo in Seed Phrase</option>
                <option value="Forgotten Password">Forgotten Password</option>
                <option value="Forgotten Passphrase (25th word)">Forgotten Passphrase (25th word)</option>
                <option value="Derivation Path Issue">Derivation Path Issue</option>
                <option value="Wallet Compromised / Scam">Wallet Compromised / Scam</option>
                <option value="Only Have Address (Seed Lost)">Only Have Address (Seed Lost)</option>
                <option value="Other / Not Sure">Other / Not Sure</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="crypto" className="form-label">
                Cryptocurrency <span className="form-required">*</span>
              </label>
              <select 
                id="crypto" 
                className="form-select"
                required
                data-testid="form-crypto"
              >
                <option value="">Select cryptocurrency...</option>
                <option value="Bitcoin (BTC)">Bitcoin (BTC)</option>
                <option value="Ethereum (ETH)">Ethereum (ETH)</option>
                <option value="Litecoin (LTC)">Litecoin (LTC)</option>
                <option value="Dogecoin (DOGE)">Dogecoin (DOGE)</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="urgency" className="form-label">
                Urgency Level
              </label>
              <select 
                id="urgency" 
                className="form-select"
                data-testid="form-urgency"
              >
                <option value="Normal">Normal (Standard Assessment)</option>
                <option value="High">High (Need Fast Response)</option>
                <option value="EMERGENCY">EMERGENCY (Wallet Compromised)</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="amount" className="form-label">
                Approximate Value at Risk <span className="form-optional">(Optional)</span>
              </label>
              <input 
                type="text" 
                id="amount" 
                className="form-input"
                placeholder="e.g., 5 BTC, £10,000, Prefer not to say"
                data-testid="form-amount"
              />
            </div>
          </div>

          <div className="form-group form-group-full">
            <label htmlFor="details" className="form-label">
              Case Details <span className="form-required">*</span>
            </label>
            <textarea 
              id="details" 
              className="form-textarea"
              rows="6"
              placeholder={"Please describe your situation in detail. Include:\n• What happened?\n• What information do you have?\n• Any relevant technical details\n\nNote: Do NOT include actual seed words, passwords, or private keys."}
              required
              data-testid="form-details"
            ></textarea>
          </div>

          <div className="form-notice">
            <Shield size={20} />
            <p>
              <strong>Confidentiality Guaranteed:</strong> All information is encrypted and treated as strictly confidential. 
              We will never ask for your actual seed phrase, private keys, or passwords.
            </p>
          </div>

          {/* 3 SEPARATE CLICK-WRAP CHECKBOXES (UK GDPR Art. 7(2)) */}
          <div style={{margin: '24px 0', padding: '20px', background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.2)'}}>
            <div style={{marginBottom: '16px'}}>
              <label style={{display: 'flex', gap: '12px', alignItems: 'flex-start', cursor: 'pointer', color: 'var(--grigio-chiaro)', fontSize: '14px', lineHeight: '1.6'}}>
                <input type="checkbox" id="chk_age" data-testid="chk-age" style={{marginTop: '4px', accentColor: '#d4af37', minWidth: '18px', minHeight: '18px'}} onChange={() => {
                  const btn = document.getElementById('submit-btn');
                  const a = document.getElementById('chk_age').checked;
                  const t = document.getElementById('chk_terms').checked;
                  const s = document.getElementById('chk_sanctions').checked;
                  btn.disabled = !(a && t && s);
                  btn.style.opacity = (a && t && s) ? '1' : '0.4';
                }} />
                <span>I confirm that I am <strong>aged 18 or over</strong> and have full legal capacity to enter into a binding agreement.</span>
              </label>
            </div>
            <div style={{marginBottom: '16px'}}>
              <label style={{display: 'flex', gap: '12px', alignItems: 'flex-start', cursor: 'pointer', color: 'var(--grigio-chiaro)', fontSize: '14px', lineHeight: '1.6'}}>
                <input type="checkbox" id="chk_terms" data-testid="chk-terms" style={{marginTop: '4px', accentColor: '#d4af37', minWidth: '18px', minHeight: '18px'}} onChange={() => {
                  const btn = document.getElementById('submit-btn');
                  const a = document.getElementById('chk_age').checked;
                  const t = document.getElementById('chk_terms').checked;
                  const s = document.getElementById('chk_sanctions').checked;
                  btn.disabled = !(a && t && s);
                  btn.style.opacity = (a && t && s) ? '1' : '0.4';
                }} />
                <span>I have read and agree to the <a href="/terms.html" target="_blank" rel="noopener" style={{color: 'var(--oro-arcana)'}}>Website Terms of Use</a>, <a href="/privacy.html" target="_blank" rel="noopener" style={{color: 'var(--oro-arcana)'}}>Privacy Policy</a> and <a href="/cookie-policy.html" target="_blank" rel="noopener" style={{color: 'var(--oro-arcana)'}}>Cookie Policy</a>.</span>
              </label>
            </div>
            <div>
              <label style={{display: 'flex', gap: '12px', alignItems: 'flex-start', cursor: 'pointer', color: 'var(--grigio-chiaro)', fontSize: '14px', lineHeight: '1.6'}}>
                <input type="checkbox" id="chk_sanctions" data-testid="chk-sanctions" style={{marginTop: '4px', accentColor: '#d4af37', minWidth: '18px', minHeight: '18px'}} onChange={() => {
                  const btn = document.getElementById('submit-btn');
                  const a = document.getElementById('chk_age').checked;
                  const t = document.getElementById('chk_terms').checked;
                  const s = document.getElementById('chk_sanctions').checked;
                  btn.disabled = !(a && t && s);
                  btn.style.opacity = (a && t && s) ? '1' : '0.4';
                }} />
                <span>I am <strong>not a Sanctioned Person</strong> and am not acting for or on behalf of a Sanctioned Person as defined in the Website Terms of Use.</span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              id="submit-btn"
              className="btn-primary" 
              disabled
              style={{fontSize: '18px', padding: '18px 48px', opacity: '0.4', cursor: 'not-allowed'}}
              data-testid="submit-assessment-btn"
              onClick={() => {
                const name = document.getElementById('name').value || 'Anonymous Client';
                const email = document.getElementById('email').value;
                const problem = document.getElementById('problem').value;
                const crypto = document.getElementById('crypto').value;
                const urgency = document.getElementById('urgency').value;
                const amount = document.getElementById('amount').value || 'Not provided';
                const details = document.getElementById('details').value;
                
                if (!email || !problem || !crypto || !details) {
                  alert('Please fill in all required fields (Email, Type of Issue, Cryptocurrency, Case Details).');
                  return;
                }
                
                const ts = new Date().toISOString();
                const ua = navigator.userAgent;
                const url = window.location.href;
                const ref = document.referrer || 'direct';
                
                const subject = encodeURIComponent('[Arcana Assessment Request] ' + problem + ' — ' + crypto);
                const body = encodeURIComponent(
                  '=== CASE ASSESSMENT REQUEST ===\n\n' +
                  'Name: ' + name + '\n' +
                  'Email: ' + email + '\n' +
                  'Issue: ' + problem + '\n' +
                  'Cryptocurrency: ' + crypto + '\n' +
                  'Urgency: ' + urgency + '\n' +
                  'Value at Risk: ' + amount + '\n\n' +
                  'Case Details:\n' + details + '\n\n' +
                  '=== CONSENT LOG (EVIDENTIAL) ===\n' +
                  'Age 18+ confirmed: YES\n' +
                  'Terms/Privacy/Cookie accepted: YES\n' +
                  'Sanctions warranty: YES\n' +
                  'Terms version: 1.0\n' +
                  'Privacy Policy version: 1.2\n' +
                  'Cookie Policy version: 1.0\n' +
                  'Consent timestamp (UTC): ' + ts + '\n' +
                  'User-Agent: ' + ua + '\n' +
                  'Page URL: ' + url + '\n' +
                  'Referrer: ' + ref + '\n' +
                  '================================\n'
                );
                
                window.location.href = 'mailto:legal@arcana-crypto.com?subject=' + subject + '&body=' + body;
              }}
            >
              SUBMIT ASSESSMENT REQUEST
            </button>
          </div>

          <p className="form-footer-text">
            You'll receive a technical evaluation within 24-48 hours. Emergency cases responded to within 30 minutes.
          </p>
        </div>
      </div>
    </section>
  );
};

const FinalCTA = () => {
  return (
    <section id="contact" className="cta-section">
      <div className="section-container">
        <h2 className="cta-title">Lost Access to Your Crypto?</h2>
        <p className="cta-subtitle">Professional recovery services start with a free case assessment.</p>
        
        <div className="cta-text">
          <p style={{marginBottom: '16px'}}><strong>No obligation. No upfront cost. No pressure.</strong></p>
          <p>Simply describe your situation, and we'll provide:</p>
          <ul className="column-list" style={{marginTop: '20px', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto'}}>
            <li>Technical feasibility evaluation</li>
            <li>Realistic success probability</li>
            <li>Clear pricing and timeline</li>
            <li>Next steps (if you choose to proceed)</li>
          </ul>
        </div>
        
        <div className="cta-buttons">
          <button className="btn-primary" style={{fontSize: '20px', padding: '20px 48px'}} onClick={() => window.location.href = '#assessment'}>
            START FREE ASSESSMENT
          </button>
          <button className="btn-secondary" onClick={() => window.location.href = '#services'}>
            VIEW ALL SERVICES
          </button>
        </div>
        
        <p className="hero-small-text" style={{marginTop: '32px'}}>
          Worldwide operations | Confidential | Professional
        </p>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="footer">
      <div className="footer-content">
        <div className="footer-top">
          <div className="footer-brand">
            <img src="/logos/logo-v4-gold.jpg" alt="ARCANA CRYPTO" className="footer-logo" />
            <p className="footer-tagline">
              Professional Cryptocurrency Recovery & Security Services
            </p>
          </div>
          
          <div className="footer-column">
            <h3>SERVICES</h3>
            <ul className="footer-links">
              <li><a href="/services">Seed Recovery</a></li>
              <li><a href="/services">Advanced Recovery</a></li>
              <li><a href="/services">Anti-Scam Services</a></li>
              <li><a href="/services">Security & Forensics</a></li>
            </ul>
          </div>
          
          <div className="footer-column">
            <h3>INFORMATION</h3>
            <ul className="footer-links">
              <li><a href="/#pricing">Pricing</a></li>
              <li><a href="/#how-it-works">How It Works</a></li>
              <li><a href="/faq">FAQ</a></li>
              <li><a href="/#assessment">Case Assessment</a></li>
            </ul>
          </div>
          
          <div className="footer-column">
            <h3><a href="/legal.html" style={{color: 'var(--oro-arcana)', textDecoration: 'none'}}>LEGAL</a></h3>
            <ul className="footer-links">
              <li><a href="/terms.html">Website Terms of Use</a></li>
              <li><a href="/privacy.html">Privacy Policy</a></li>
              <li><a href="/cookie-policy.html">Cookie Policy</a></li>
              <li><a href="/geographical-limits.html">Geographical Limits</a></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); if(typeof window.ArcanaCookieBanner !== 'undefined') window.ArcanaCookieBanner.openSettings(); }}>Cookie Settings</a></li>
            </ul>
          </div>
        </div>
        
        <div className="footer-bottom">
          <div className="footer-contact">
            <p style={{color: 'var(--grigio-chiaro)', marginBottom: '16px', fontSize: '16px', fontWeight: '600'}}>
              <strong>CONTACT METHODS:</strong>
            </p>
            <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
              <p style={{color: 'var(--grigio-chiaro)', marginBottom: '0'}}>
                <strong>📧 Email:</strong>{' '}
                <a href="mailto:legal@arcana-crypto.com" className="footer-email">
                  legal@arcana-crypto.com
                </a>
              </p>
              <p style={{color: 'var(--grigio-chiaro)', marginBottom: '0'}}>
                <strong>💬 WhatsApp:</strong>{' '}
                <a 
                  href="https://wa.me/447835822143?text=Hello%20Arcana%20Crypto,%20I%20need%20help%20with%20a%20wallet%20recovery." 
                  className="footer-email"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  +44 7835 822143
                </a>
              </p>
              <p style={{color: 'var(--grigio-chiaro)', marginBottom: '0'}}>
                <strong>🔐 Telegram:</strong>{' '}
                <a 
                  href="https://t.me/Arcana_Crypto_Lab" 
                  className="footer-email"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  @Arcana_Crypto_Lab
                </a>
              </p>
              <p style={{color: 'var(--grigio-medio)', fontSize: '14px', marginTop: '8px'}}>
                📱 Signal: Available by appointment
              </p>
            </div>
          </div>
          
          <div className="footer-copyright">
            <p style={{fontWeight: '600', marginBottom: '8px'}}>
              © 2025-2026 ARCANA CRYPTO LTD - All Rights Reserved
            </p>
            <p style={{fontSize: '12px', marginBottom: '4px'}}>
              Registered in England and Wales | Company No. 16371124
            </p>
            <p style={{fontSize: '12px', marginBottom: '4px'}}>
              Registered office: 12 Pullman Gardens, London SW15 3DF, United Kingdom
            </p>
            <p style={{fontSize: '12px', marginBottom: '4px'}}>
              ICO Registration: ZC133290
            </p>
            <p style={{fontSize: '12px', marginBottom: '4px', color: 'var(--oro-arcana)'}}>
              PI Insured £1M | Cyber Insured £100K — Hiscox
            </p>
          </div>
          
          <div className="footer-legal">
            <p>
              Arcana Crypto provides technical services only. We do not provide custody, 
              financial advice, or legal counsel. All services are subject to assessment 
              and contractual terms.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Back to Top Button
const BackToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      {isVisible && (
        <button className="back-to-top" onClick={scrollToTop}>
          <ArrowUp size={24} />
        </button>
      )}
    </>
  );
};

const Home = () => {
  return (
    <div>
      <EmergencyBanner />
      <Navigation />
      <HeroSection />
      <TrustBar />
      <AboutUs />
      <WhatWeDo />
      <ServiceCategories />
      <WhyArcana />
      <HowItWorks />
      <PricingOverview />
      <CompletePricing />
      <WhatMakesUsDifferent />
      <SupportedCrypto />
      <AntiScamServices />
      <OperationalPrinciples />
      <FAQ />
      <AssessmentForm />
      <FinalCTA />
      <Footer />
      <FloatingContact />
      <BackToTop />
    </div>
  );
};

// 404 Not Found page — fixes soft-404s (noindex is injected by the inline script in index.html)
const NotFound = () => {
  useEffect(() => {
    document.title = "404 — Page Not Found | ARCANA CRYPTO";
  }, []);

  return (
    <div
      data-testid="not-found-page"
      style={{
        minHeight: "100vh",
        background: "#0a0a0a",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px"
      }}
    >
      <div
        style={{
          maxWidth: "560px",
          textAlign: "center",
          padding: "48px 40px",
          borderRadius: "16px",
          border: "1px solid rgba(212, 175, 55, 0.25)",
          background: "rgba(255, 255, 255, 0.03)",
          backdropFilter: "blur(16px)"
        }}
      >
        <div style={{ fontSize: "72px", fontWeight: 800, color: "#d4af37", letterSpacing: "4px", marginBottom: "8px" }}>404</div>
        <h1 style={{ color: "#f4e4c1", fontSize: "24px", marginBottom: "16px" }}>Page not found</h1>
        <p style={{ color: "rgba(255,255,255,0.65)", lineHeight: 1.6, marginBottom: "32px" }}>
          The page you are looking for doesn't exist or has been moved.
        </p>
        <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
          <a
            data-testid="not-found-home-link"
            href="/"
            style={{
              padding: "12px 28px",
              borderRadius: "999px",
              background: "linear-gradient(135deg, #d4af37, #e5c158)",
              color: "#0a0a0a",
              fontWeight: 700,
              textDecoration: "none"
            }}
          >
            Back to Home
          </a>
          <a
            data-testid="not-found-guides-link"
            href="/resources-seo.html"
            style={{
              padding: "12px 28px",
              borderRadius: "999px",
              border: "1px solid rgba(212,175,55,0.4)",
              color: "#d4af37",
              fontWeight: 600,
              textDecoration: "none"
            }}
          >
            Recovery Guides
          </a>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/crypto-recovery-service-uk" element={<CryptoRecoveryUKPage />} />
          <Route path="/seed-phrase-recovery" element={<SeedPhraseRecoveryPage />} />
          <Route path="/wallet-password-recovery" element={<WalletPasswordRecoveryPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/resources/seed-phrase-missing-1-word" element={<SeedPhraseMissing1Word />} />
          <Route path="/resources/seed-phrase-typos-wrong-order" element={<SeedPhraseTyposWrongOrder />} />
          <Route path="/resources/wallet-password-recovery-owner-authorised" element={<WalletPasswordRecoveryOwnerAuthorised />} />
          <Route path="/resources/funds-not-showing-derivation-paths" element={<FundsNotShowingDerivationPaths />} />
          <Route path="/resources/compromised-wallet-first-30-minutes" element={<CompromisedWalletFirst30Minutes />} />
          <Route path="/resources/passphrase-25th-word-recovery" element={<PassphraseRecoveryGuide />} />
          <Route path="/resources/hardware-wallet-ledger-trezor-recovery" element={<HardwareWalletRecoveryGuide />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="/audit-react" element={<AuditPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
